import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import * as CompanyActions from "../../../../store/actions/company";
import { Button, Card, Steps, Tooltip } from "antd";
import Questionaire from "./pages/questionaire";
import Financials from "./pages/financials";
import { multipleTypes } from "../../constants/input-types";
import { BeatLoader } from "react-spinners";
import { FormStepsSkeleton } from "../../../../components/page-skeleton";
import NoCompanySymbol from "../../../../assets/images/nocompany.png";
import { toast } from "react-toastify";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Delete, Edit } from "@mui/icons-material";
import { AddModal, DeleteCompanyModal } from "./components/modal";
import { debounce, set } from "lodash";
import {
  getGoalQuestionValue,
  hasGoalQuestionValue,
} from "./helpers/goal-question-value";
import { upsertGoalQuestionInOrder } from "./helpers/goal-question-order";
import { startAlgo } from "../../../../store/actions/algo";
import { setProfile } from "../../../../store/actions/user";

const { Step } = Steps;
const FLORENCE_NAV_HIGHLIGHT_EVENT = "highlight-florence-nav";

const CompanySubmissionPage = (props) => {
  // STATES
  const [dataSet, setDataSet] = useState({});
  const [inputs, setInputs] = useState([]);
  const [state, setState] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [draftMode, setDraftMode] = useState(false);
  const [deleteModalPreview, setDeleteModalPreview] = useState(false);
  const [editModalPreview, setEditModalPreview] = useState(false);
  const [discardLoader, setDiscardLoader] = useState(false);
  const [populateLoader, setPopulateLoader] = useState({
    goals: false,
    attributes: false,
    financials: false,
  });
  const [populateFieldsLoader, setPopulateFieldsLoader] = useState({});
  const [populateOptionsTrigger, setPopulateOptionsTrigger] = useState(0);
  const inputsRef = useRef([]);
  const stateRef = useRef({});
  const goalDraftInputsRef = useRef({});
  const discardJustHappenedRef = useRef(false);

  // REDUX DATA
  const user = useSelector((state) => state.UserReducer.profile);
  const { selected_company: selectedCompany, companies } = useSelector(
    (state) => state.CompanyReducer,
  );

  // CONST VALS
  const { http } = global.services;
  const history = useHistory();
  const dispatch = useDispatch();
  const section =
    currentStep == 0 ? "goals" : currentStep == 1 ? "attributes" : "financials";
  const anyFieldsLoading = useMemo(() => {
    return Object.values(populateFieldsLoader[section] || {}).some((val) => val === true);
  }, [populateFieldsLoader, section]);
  const disableButton = useMemo(
    () =>
      state[
        currentStep == 0 ? "goals" : currentStep == 1 ? "attributes" : "financials"
      ]?.every((obj) =>
        typeof obj?.value == "number" ||
        obj?.question[0]?.includes("optional") ||
        obj?.optional
          ? true
          : obj?.value?.length,
      ),
    [state, currentStep],
  );

  // HELPERS
  const genererateSubmitInputs = () => {
    const user_inputs = [...inputsRef.current];
    Object.keys(stateRef.current).map((k, i) => {
      stateRef.current[k].map((q) => {
        if (i == 0) {
          if (Array.isArray(q.value)) {
            user_inputs[i][q.key] = ["block", q.value];
          } else if (q.value) {
            user_inputs[i][q.key] = ["block", q.value];
          }
        } else {
          if (typeof q.value == "object") {
            user_inputs[i][q.key] = q.value;
          } else if (q.value) {
            user_inputs[i][q.key] = [q.value];
          }
        }
      });
    });
    return user_inputs;
  };

  const initQuestions = async (fromPopulate) => {
    let { data } = await http.get("question");
    let { data: draftData } = await http.get(`company-draft/${selectedCompany.id}`);
    setDraftMode(draftData?.draft ?? false);
    setLoading(false);
    setDiscardLoader(false);
    setDataSet(data.data);
    if (!fromPopulate) setCurrentStep(0);
    init(data.data, draftData?.data?.user_input ?? selectedCompany?.user_input ?? {});
  };

  const saveDraftHelper = () => {
    const user_input = genererateSubmitInputs();
    http
      .post(`company-draft/${selectedCompany.id}`, {
        user_input,
      })
      .catch((err) => {});
  };

  const discardDraftHandler = () => {
    if (!draftMode) return;
    setDiscardLoader(true);
    discardJustHappenedRef.current = true;
    saveDraft.cancel?.();
    http
      .delete(`company-draft/${selectedCompany.id}`)
      .then(() => {
        setDraftMode(false);
        toast.success("Draft Discarded Successfully");
        initQuestions();
      })
      .catch(({ data }) => {
        setDiscardLoader(false);
        toast.error(data?.error ?? "Something went wrong");
      });
  };

  const populateQuestionsHelper = (description, section) => {
    const questions = state[section].filter((q) => !q.base_field && !q.optional);
    const loadingKeys = questions.map((q) => q.key);
    setPopulateLoader((prev) => ({
      ...prev,
      [section]: true,
    }));
    http
      .post(`populate-company-question/${selectedCompany.id}`, {
        description,
        section,
      })
      .then(({ data }) => {
        setPopulateFieldsLoader((prev) => {
          const newState = {
            ...prev,
            [section]: { ...(prev[section] || {}) }, // initialize if missing
          };
          loadingKeys.forEach((key) => {
            newState[section][key] = true;
          });
          return newState;
        });
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || `Error populating ${section}`);
        setPopulateLoader((prev) => ({
          ...prev,
          [section]: false,
        }));
      });
  };

  const getPopulateStatus = (onSuccess) => {
    http
      .get(`populate-questions-status/${selectedCompany.id}?section=${section}`)
      .then(({ data }) => {
        const newLoadingState = { ...populateFieldsLoader };
        if (data.status === "completed") {
          setPopulateLoader((prev) => ({
            ...prev,
            [section]: false,
          }));
          if (onSuccess) onSuccess();
        }
        if (data.status === "failed") {
          setPopulateLoader((prev) => ({
            ...prev,
            [section]: false,
          }));
          newLoadingState[section] = {};
          toast.error(`Error populating ${section} fields. Please try again.`);
          setPopulateFieldsLoader(newLoadingState);
          if (onSuccess) onSuccess();
          return;
        }
        if (!newLoadingState[section]) newLoadingState[section] = {};

        // Use latest state (ref) so we never merge into stale closure state when
        // polling runs again (e.g. second populate with different description)
        const latestState =
          stateRef.current && Object.keys(stateRef.current).length > 0
            ? stateRef.current
            : state;

        // Parse value: backend may send arrays/objects as JSON strings
        const parseValue = (val) => {
          if (val == null || val === "none" || val === '["none"]') return null;
          if (typeof val === "string" && (val.startsWith("[") || val.startsWith("{"))) {
            try {
              return JSON.parse(val);
            } catch {
              return val;
            }
          }
          return val;
        };

        // Update section immutably so Questionaire's useEffect runs and rebuilds
        // dependent options (broad/narrow specialty, NAICS sub-dropdown)
        const sectionPayload = data.data && data.data[section] ? data.data[section] : {};
        const sectionData = (latestState[section] || []).map((q) => {
          // Backend may use keys without "_input" (e.g. primary_sector vs primary_sector_input)
          const dataKey =
            sectionPayload[q.key] ??
            sectionPayload[q.key.replace(/_input$/, "")] ??
            sectionPayload[q.key.replace(/-/g, "_")];
          const value = parseValue(dataKey);
          if (value != null) {
            newLoadingState[section][q.key] = false;
            return { ...q, value };
          }
          return q;
        });

        const inputsData = { ...latestState, [section]: sectionData };
        setState(inputsData);
        stateRef.current = inputsData;
        setPopulateFieldsLoader(newLoadingState);
        // Force Questionaire to rebuild dependent options (broad/narrow/NAICS) after status API update
        setPopulateOptionsTrigger((prev) => prev + 1);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // HANDLERS
  const goBack = () => {
    history.goBack();
  };

  const saveDraft = useCallback(
    debounce(() => {
      saveDraftHelper();
    }, 1000),
    [selectedCompany],
  );

  const setInputsHandler = (data) => {
    setState(data);
    stateRef.current = data;
    // Avoid re-saving draft right after discard (e.g. when child effects sync state).
    if (discardJustHappenedRef.current) {
      discardJustHappenedRef.current = false;
      return;
    }
    setDraftMode(true);
    saveDraft();
  };

  const editCompanyPreviewHandler = () => {
    setEditModalPreview(!editModalPreview);
  };

  const deleteCompanyPreviewHandler = () => {
    setDeleteModalPreview(!deleteModalPreview);
  };

  const getGoalNextLabels = (question, questionsList) => {
    if (!hasGoalQuestionValue(question.value)) {
      return [];
    }

    if (question.label === "question-one" && question.value === "Acquire") {
      return questionsList
        .filter((item) => item.key.includes("acquire"))
        .map((item) => item.label);
    }

    const labels = [];
    const selectedOptions = Array.isArray(question.value)
      ? []
      : (question.options?.filter((option) => option.value === question.value) ?? []);

    selectedOptions.forEach((option) => {
      if (Array.isArray(option.label)) {
        labels.push(...option.label);
        return;
      }

      if (typeof option.label === "string" && !option.label.includes("remove@")) {
        labels.push(option.label);
      }
    });

    if (Array.isArray(question.target)) {
      question.target.forEach((target) => {
        const shouldInclude =
          target.value === "all" ||
          (Array.isArray(question.value) &&
            question.value.some(
              (item) => String(item).toLowerCase() === String(target.value).toLowerCase(),
            ));

        if (shouldInclude && target.label) {
          labels.push(target.label);
        }
      });
    }

    return [...new Set(labels.filter(Boolean))];
  };

  const buildGoalQuestions = (goalQuestions = [], goalInputs = {}) => {
    const visibleQuestions = [];
    const addedKeys = new Set();

    const addQuestion = (question) => {
      if (!question || addedKeys.has(question.key)) return;

      addedKeys.add(question.key);
      visibleQuestions.push({
        ...question,
        value: getGoalQuestionValue(question, goalInputs),
      });
    };

    const addQuestionsByLabel = (label) => {
      goalQuestions
        .filter((question) => question.label === label)
        .forEach((question) => addQuestion(question));
    };

    goalQuestions
      .filter((question) => question.parent.length === 0)
      .forEach((question) => addQuestion(question));

    for (let i = 0; i < visibleQuestions.length; i++) {
      getGoalNextLabels(visibleQuestions[i], goalQuestions).forEach((label) => {
        addQuestionsByLabel(label);
      });
    }

    // Restore conditional Florence questions from draft in DB order (industry, then ranking focus).
    let orderedQuestions = visibleQuestions;
    ["industry_segmentation", "user_preferred_ranking_focus"].forEach((key) => {
      const question = goalQuestions.find((item) => item.key === key);
      if (!question) return;

      const saved = goalInputs?.[key];
      const hasExplicitDraft = saved != null && !saved?.includes?.("none");
      const value = getGoalQuestionValue(question, goalInputs);
      const shouldRestore =
        key === "user_preferred_ranking_focus" && hasExplicitDraft
          ? true
          : hasGoalQuestionValue(value);
      if (!shouldRestore) return;

      addedKeys.add(key);
      orderedQuestions = upsertGoalQuestionInOrder(
        orderedQuestions,
        question,
        goalQuestions,
        value,
      );
    });

    return orderedQuestions;
  };

  const init = (params, user_input) => {
    let inp = [];
    let questions = {};
    goalDraftInputsRef.current = user_input?.[0] || {};

    if (user_input && user_input.length > 0) {
      Object.keys(params).map((k, index) => {
        inp[index] = {};
        questions[k] = [];
        params[k].map((q, i) => {
          let question = q;
          inp[index][q.key] = ["none"];
          if (index == 0) {
            return;
          }

          if (user_input[index][q.key]?.includes("none")) {
            if (multipleTypes.includes(q.type)) question.value = [];

            questions[k].push(question);
          } else {
            if (multipleTypes.includes(q.type)) {
              if (user_input[index][q.key]) {
                question.value = user_input[index][q.key] ?? [];
              }
            } else {
              if (user_input[index][q.key]) {
                question.value = user_input[index][q.key][0];
              }
            }
            questions[k].push(question);
          }
        });

        if (index === 0) {
          questions[k] = buildGoalQuestions(params[k], user_input[index]);
        }
      });
    } else {
      Object.keys(params).map((k, index) => {
        inp[index] = {};
        questions[k] = [];
        params[k].map((q) => {
          let question = q;
          inp[index][q.key] = ["none"];
          if (q.parent.length == 0) {
            questions[k].push(question);
          }
        });
      });
    }

    setState(questions);
    stateRef.current = questions;
    setInputs(inp);
    inputsRef.current = inp;
  };
  const runFlorence = async (companyId) => {
    const florenceAccess = user?.free_trial
      ? user?.remaining_buyers_trial
      : user?.algo_access;
    if (!florenceAccess || !companyId) return;

    try {
      const { data } = await http.put(`company-buyers/${companyId}`);
      if (data?.remaining_buyers_trial !== undefined) {
        dispatch(
          setProfile({
            ...user,
            remaining_buyers_trial: data.remaining_buyers_trial,
          }),
        );
      }
      // Keep progress modal closed; Florence still runs in the background.
      dispatch(
        startAlgo({ companyId, algoId: data.algo, preview: false }),
      );
      window.dispatchEvent(new CustomEvent(FLORENCE_NAV_HIGHLIGHT_EVENT));
    } catch (err) {
      toast.error(err?.response?.data?.error ?? "Something went wrong");
    }
  };

  const submit = async () => {
    setSubmitLoader(true);
    const isFirstSubmission = !selectedCompany?.user_input;
    const user_input = genererateSubmitInputs();
    try {
      let { data } = await http.put("user-company", {
        id: selectedCompany.id,
        company_name: selectedCompany.company_name,
        user_input,
      });
      dispatch(CompanyActions.setSelectedCompany(data.data));
      dispatch(CompanyActions.updateCompany(data.data));
      setSubmitLoader(false);
      setDraftMode(false);
      history.push("/company-detail");
      if (isFirstSubmission) {
        runFlorence(selectedCompany.id);
      }
    } catch (err) {
      setSubmitLoader(false);
      if (err.response && err.response.data) {
        toast.error(err.response.data.error);
      } else {
        toast.error("Unknown Error");
      }
    }
  };

  const onClickAction = (action) => {
    if (action === "next") {
      setCurrentStep(currentStep + 1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  // HOOKS
  useEffect(() => {
    setInputs([]);
    inputsRef.current = [];
    setState({});
    stateRef.current = {};
    if (!selectedCompany?.id) return;
    setLoading(true);
    initQuestions();
  }, [selectedCompany]);

  useEffect(() => {
    let timer;
    if (anyFieldsLoading) {
      timer = setInterval(() => {
        getPopulateStatus(() => clearInterval(timer));
      }, 5000);
    }
    return () => clearInterval(timer);
  }, [anyFieldsLoading, currentStep]);

  return (
    <React.Fragment>
      {selectedCompany && selectedCompany?.id ? (
        <>
          <div className="top-header reduced">
            <h2 className="page-title">
              <button className="back-btn" onClick={goBack}>
                <ArrowLeftOutlined />
              </button>
              Company Brief
              {selectedCompany?.created_by_user && (
                <span className="action-items">
                  <Tooltip title="Edit Company">
                    <button className="edit-btn" onClick={editCompanyPreviewHandler}>
                      <Edit />
                    </button>
                  </Tooltip>
                  <Tooltip title="Delete Company">
                    <button className="delete-btn" onClick={deleteCompanyPreviewHandler}>
                      <Delete />
                    </button>
                  </Tooltip>
                </span>
              )}
              {draftMode && (
                <Tooltip title="This submission is not visible to others until it is submitted.">
                  <span className="draft-badge">Draft</span>
                </Tooltip>
              )}
            </h2>
            <div className="action-box" style={{ minHeight: 45 }}>
              {draftMode && (
                <button className="delete-toggle" onClick={discardDraftHandler}>
                  {discardLoader ? (
                    <BeatLoader size={8} color="#ff4d4f" />
                  ) : (
                    "Discard Changes"
                  )}
                </button>
              )}
            </div>
          </div>
          <p className="company-brief-helper-text">
            Tell Florence about the company, deal, or opportunity you want to build a
            target list for.
          </p>
        </>
      ) : (
        <div className="company-submission-add">
          <img src={NoCompanySymbol} height={100} width={100} />
          <strong>
            <h2>Company Brief</h2>
          </strong>
          {/* <Submission
            currentCompany={selectedCompany}
            company={company}
            setCompany={setCompany}
          /> */}
        </div>
      )}
      {!!companies?.length && (
        <>
          <div className="spacer"></div>
          <Steps size="small" current={currentStep}>
            <Step key={"Goals"} title="Goals" />
            <Step key={"Attributes"} title="Attributes" />
            <Step key={"Financials"} title="Financials" />
          </Steps>

          <div className="steps-content">
            {isLoading ? (
              <FormStepsSkeleton height={300} />
            ) : (
              <>
                {currentStep === 0 && (
                  <Questionaire
                    title="goals"
                    populateLoader={populateLoader}
                    populateFieldsLoader={populateFieldsLoader[section] || {}}
                    populateHelper={populateQuestionsHelper}
                    currentStep={currentStep}
                    questions={dataSet.goals || []}
                    sets={state}
                    dataSet={dataSet}
                    state={state.goals}
                    setState={setInputsHandler}
                    populateOptionsTrigger={populateOptionsTrigger}
                    goalInputs={goalDraftInputsRef.current}
                  />
                )}
                {currentStep === 1 && (
                  <Questionaire
                    title="attributes"
                    populateLoader={populateLoader}
                    populateFieldsLoader={populateFieldsLoader[section] || {}}
                    populateHelper={populateQuestionsHelper}
                    currentStep={currentStep}
                    questions={dataSet.attributes || []}
                    sets={state}
                    state={state.attributes}
                    setState={setInputsHandler}
                    populateOptionsTrigger={populateOptionsTrigger}
                  />
                )}
                {currentStep === 2 && (
                  <Financials
                    populateHelper={populateQuestionsHelper}
                    questions={dataSet.financials || []}
                    sets={state}
                    state={state.financials}
                    setState={setInputsHandler}
                    currentStep={currentStep}
                  />
                )}
              </>
            )}
          </div>
          {!isLoading && (
            <div className="steps-action">
              <div className="flex-1"></div>
              {selectedCompany && selectedCompany.id && (
                <div>
                  {currentStep > 0 && (
                    <Button
                      className="btn"
                      type="text"
                      style={{ margin: "0 8px" }}
                      onClick={() => onClickAction("prev")}
                    >
                      Back
                    </Button>
                  )}
                  {currentStep < 2 && (
                    <Button
                      className="btn btn-primary"
                      disabled={!disableButton}
                      onClick={() => onClickAction("next")}
                    >
                      Next
                    </Button>
                  )}
                  {currentStep === 2 && (
                    <Button
                      className="btn btn-primary"
                      disabled={!disableButton}
                      type="primary"
                      onClick={submit}
                    >
                      {submitLoader ? (
                        <BeatLoader color="#fff" loading={submitLoader} size={8} />
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {editModalPreview && (
        <AddModal
          isEdit={true}
          data={selectedCompany}
          show={editModalPreview}
          onClose={() => setEditModalPreview(false)}
          currentCompany={selectedCompany}
          isOrganization={user.organization}
        />
      )}
      <DeleteCompanyModal
        navigateToListing
        preview={deleteModalPreview}
        selectedCompany={selectedCompany}
        previewHandler={() => setDeleteModalPreview(false)}
      />
    </React.Fragment>
  );
};

export default CompanySubmissionPage;
